import produce from 'immer';
import { uniq, uniqBy } from 'lodash';
import { handleActions } from 'redux-actions';
import actions from './actions';
import moment from '../../../lib/moment';
import { setUncheckableTableTransferList, updateStoreWithPath } from '../../../lib/common';
import { setApplyCouponDiscount, setCouponGenerateLimitType } from '../../../lib/utils/couponUtils';
import {
  COUPON_CREATE_MODEL,
  COUPON_DETAIL_MODEL,
  COUPON_GENERATE_MODEL,
  COUPON_GENERATE_USER_FILTER_OPTION_MODEL,
  COUPON_ISSUE_HISTORY_DETAIL_FILTER_OPTION_MODEL,
  COUPON_ISSUE_HISTORY_DETAIL_SORT_OPTION_MODEL,
  COUPON_ISSUE_HISTORY_LIST_SEARCH_DATA_MODEL,
  COUPON_ISSUE_HISTORY_LIST_SEARCH_OPTION_MODEL,
  COUPON_LIST_FILTER_OPTION_MODEL,
  COUPON_LIST_SEARCH_DATA_MODEL,
  COUPON_LIST_SEARCH_OPTION_MODEL,
  COUPON_LIST_SORT_OPTION_MODEL,
  COUPON_SEARCH_MODAL_MODEL,
} from './models';
import { _LIST_PAGINATION_MODEL, LIST_PAGINATION_MODEL, LIST_SUMMARY_MODEL } from '../app/models';
import {
  COUPON_AVAILABLE_TYPE,
  COUPON_INFO_TAB_INDEX,
  COUPON_PERIOD_TYPE,
} from '../../../constants/service/app/couponConstants';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  쿠폰 리스트에서 사용되는 MODEL
   */
  couponList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: COUPON_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: COUPON_LIST_SEARCH_OPTION_MODEL,
    /**
     *  필터 옵션
     */
    filterOption: COUPON_LIST_FILTER_OPTION_MODEL,
    /**
     *  정렬 옵션
     */
    sortOption: COUPON_LIST_SORT_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: LIST_PAGINATION_MODEL,
    /**
     *  집계 데이터
     */
    summary: LIST_SUMMARY_MODEL,
  },

  /**
   *  쿠폰 상세
   */
  couponDetail: COUPON_DETAIL_MODEL,

  /**
   *  쿠폰 등록
   */
  couponCreate: COUPON_CREATE_MODEL,

  /**
   *  쿠폰 발급
   */
  couponGenerate: COUPON_GENERATE_MODEL,

  /**
   *  쿠폰 발급 내역 리스트
   */
  couponIssueHistoryList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: COUPON_ISSUE_HISTORY_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: COUPON_ISSUE_HISTORY_LIST_SEARCH_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  admin SELECT 리스트
   */
  adminSelectList: [],

  /**
   *  쿠폰 발급 내역 상세
   */
  couponIssueHistoryDetail: {
    /**
     *  쿠폰 발급 내역 정보
     */
    couponIssueHistoryInfo: {
      /**
       *  발급 내역 id
       */
      couponIssueHistoryId: null,
      /**
       *  발급한 관리자
       */
      adminName: null,
      /**
       *  메모
       */
      memo: null,
      /**
       *  쿠폰 발급 데이터 생성일
       */
      createDate: null,
    },
    /**
     *  쿠폰 발급 내역 아이템 리스트
     */
    couponIssueHistoryItemList: {
      /**
       *  리스트
       */
      list: [],
      /**
       *  필터 옵션
       */
      filterOption: COUPON_ISSUE_HISTORY_DETAIL_FILTER_OPTION_MODEL,
      /**
       *  정렬 옵셥
       */
      sortOption: COUPON_ISSUE_HISTORY_DETAIL_SORT_OPTION_MODEL,
      /**
       *  리스트 pagination
       */
      pagination: LIST_PAGINATION_MODEL,
    },
  },

  /**
   *  쿠폰 자세히 보는 모달
   */
  couponDetailModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  발급할 쿠폰 리스트 모달
   */
  searchCouponListModal: {
    isOpen: false,
    data: {
      listData: COUPON_SEARCH_MODAL_MODEL,
    },
  },

  /**
   *  엑셀 쿠폰 일괄 발급 모달
   */
  excelBulkGenerateCouponModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  쿠폰 할인 편집 모달
   */
  editCouponDiscountModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  쿠폰 적용가능 상품 리스트 모달
   */
  couponApplyAvailableProductListModal: {
    isOpen: false,
    data: {
      listData: {
        list: [],
        pagination: LIST_PAGINATION_MODEL,
      },
    },
  },

  /**
   *  쿠폰 사용 가능한 상품 유효성 검사 성공 모달
   */
  excelUploadCouponProductValidSuccessModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  쿠폰 사용 가능한 상품 유효성 검사 실패 모달
   */
  excelUploadCouponProductValidFailModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  쿠폰 사용 가능한 브랜드 유효성 검사 성공 모달
   */
  excelUploadCouponBrandValidSuccessModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  쿠폰 사용 가능한 브랜드 유효성 검사 실패 모달
   */
  excelUploadCouponBrandValidFailModal: {
    isOpen: false,
    data: {},
  },
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  [쿠폰] 기획전 데이터 변경하기
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  [쿠폰 리스트] 쿠폰 리스트 요청하기
     */
    [actions.GET_COUPON_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰 리스트
         */
        draft.couponList = {
          ...draft.couponList,
          ...action.payload.couponList,
        };
      });
    },

    /**
     *  [쿠폰 생성] 상품 리스트 EXCEL 업로드
     */
    [actions.SET_COUPON_PRODUCT_ITEM_LIST_EXCEL_UPLOAD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택된 상품 리스트
         */
        draft.couponCreate.couponProductLinkInfo.selectedItemList.list = action.payload.productList;

        /**
         *  선택된 상품 아이템의 중복 제거하기
         */
        draft.couponCreate.couponProductLinkInfo.selectedItemList.list = uniqBy(
          draft.couponCreate.couponProductLinkInfo.selectedItemList.list,
          'id',
        );

        draft.couponCreate.couponProductLinkInfo.selectedItemList.idList =
          draft.couponCreate.couponProductLinkInfo.selectedItemList.list.map((item) => item.id);

        /**
         *  선택할 상품 아이템 중에 이미 설정된 상품 아이템인 경우에는
         *  상품 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.couponCreate.couponProductLinkInfo.itemList.list = setUncheckableTableTransferList({
          list: draft.couponCreate.couponProductLinkInfo.itemList.list,
          selectedIdList: draft.couponCreate.couponProductLinkInfo.selectedItemList.idList,
        });

        /**
         *  선택된 연결할 아이템들의 storeId 리스트
         */
        const selectedItemStoreIdList = draft.couponCreate.couponProductLinkInfo.selectedItemList.list.map(
          (item) => item.storeId,
        );

        draft.couponCreate.couponProductLinkInfo.selectedItemBrandCount = uniq(selectedItemStoreIdList).length;
      });
    },

    /**
     *  [쿠폰 생성] 브랜드 리스트 EXCEL 업로드
     */
    [actions.SET_COUPON_BRAND_ITEM_LIST_EXCEL_UPLOAD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택된 브랜드 리스트
         */
        draft.couponCreate.couponBrandLinkInfo.selectedItemList.list = action.payload.brandList;

        /**
         *  선택된 브랜드 아이템의 중복 제거하기
         */
        draft.couponCreate.couponBrandLinkInfo.selectedItemList.list = uniqBy(
          draft.couponCreate.couponBrandLinkInfo.selectedItemList.list,
          'id',
        );

        draft.couponCreate.couponBrandLinkInfo.selectedItemList.idList =
          draft.couponCreate.couponBrandLinkInfo.selectedItemList.list.map((item) => item.id);

        /**
         *  선택할 브랜드 아이템 중에 이미 설정된 브랜드 아이템인 경우에는
         *  브랜드 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.couponCreate.couponBrandLinkInfo.itemList.list = setUncheckableTableTransferList({
          list: draft.couponCreate.couponBrandLinkInfo.itemList.list,
          selectedIdList: draft.couponCreate.couponBrandLinkInfo.selectedItemList.idList,
        });
      });
    },

    /**
     *  [쿠폰 편집] 쿠폰 정보 가져오기
     */
    [actions.GET_COUPON_INFO_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰 정보 탭이 먼저 떠야한다.
         */
        draft.couponCreate.currentTabKey = COUPON_INFO_TAB_INDEX;
        /**
         *  쿠폰 상세
         */
        draft.couponCreate.couponInfo = action.payload.couponInfo;
        /**
         *  쿠폰 기간 타입 = 시작 종료 날짜 지정 기준
         */
        if (action.payload.couponInfo.periodType === COUPON_PERIOD_TYPE.START_AND_END_DATE) {
          /**
           *  쿠폰 유효기간 설정
           */
          draft.couponCreate.couponInfo.period.startDate = moment(action.payload.couponInfo.period.startDate);
          draft.couponCreate.couponInfo.period.endDate = moment(action.payload.couponInfo.period.endDate);
        }

        /**
         *  generateLimitDay 에 따라 발급 제한 횟수 타입을 설정한다.
         */
        draft.couponCreate.couponInfo.generateLimitType = setCouponGenerateLimitType(
          action.payload.couponInfo.generateLimitDay,
        );

        /**
         *  쿠폰 유형에 따라 데이터 추가해주는게 다르다
         */
        if (action.payload.couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_PRODUCT_COUPON) {
          /**
           *  쿠폰 사용 가능한 상품 정보
           */
          draft.couponCreate.couponProductLinkInfo = {
            ...draft.couponCreate.couponProductLinkInfo,
            ...action.payload.couponProductLinkInfo,
          };

          /**
           *  쿠폰적용가, 쿠폰적용 할인액 데이터 추가
           */
          draft.couponCreate.couponProductLinkInfo.selectedItemList.list = setApplyCouponDiscount(
            draft.couponCreate.couponProductLinkInfo.selectedItemList.list,
            action.payload.couponInfo.discountType,
            action.payload.couponInfo.discountAmount,
          );

          /**
           *  업로드 아이템 중에 선택된 상품 아이템인 경우에는
           *  상품 아이템 선택 부분에서 이동을 할 수 없도록 하기
           */
          draft.couponCreate.couponProductLinkInfo.itemList.list = setUncheckableTableTransferList({
            list: draft.couponCreate.couponProductLinkInfo.itemList.list,
            selectedIdList: draft.couponCreate.couponProductLinkInfo.selectedItemList.idList,
          });

          /**
           *  상품 1차 카테고리
           */
          draft.couponCreate.mainProductCategorySelectList = action.payload.mainProductCategorySelectList;
          /**
           *  상품 2차 카테고리
           */
          draft.couponCreate.subProductCategorySelectList = action.payload.subProductCategorySelectList;
        } else if (action.payload.couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_BRAND_COUPON) {
          /**
           *  쿠폰 사용 가능한 브랜드 정보
           */
          draft.couponCreate.couponBrandLinkInfo = {
            ...draft.couponCreate.couponBrandLinkInfo,
            ...action.payload.couponBrandLinkInfo,
          };

          /**
           *  업로드 아이템 중에 선택된 브랜드 아이템인 경우에는
           *  브랜드 아이템 선택 부분에서 이동을 할 수 없도록 하기
           */
          draft.couponCreate.couponBrandLinkInfo.itemList.list = setUncheckableTableTransferList({
            list: draft.couponCreate.couponBrandLinkInfo.itemList.list,
            selectedIdList: draft.couponCreate.couponBrandLinkInfo.selectedItemList.idList,
          });
        }
      });
    },

    /**
     *  [쿠폰 편집] 쿠폰 정보 가져오기
     */
    [actions.GET_COUPON_COPY_INFO_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰 정보 탭이 먼저 떠야한다.
         */
        draft.couponCreate.currentTabKey = COUPON_INFO_TAB_INDEX;
        /**
         *  쿠폰 상세
         *  code 는 가져오지 말아야한다.
         */
        draft.couponCreate.couponInfo = {
          ...action.payload.couponInfo,
          code: null,
        };

        /**
         *  쿠폰 기간 타입 = 시작 종료 날짜 지정 기준
         */
        if (action.payload.couponInfo.periodType === COUPON_PERIOD_TYPE.START_AND_END_DATE) {
          /**
           *  쿠폰 유효기간 설정
           */
          draft.couponCreate.couponInfo.period.startDate = moment(action.payload.couponInfo.period.startDate);
          draft.couponCreate.couponInfo.period.endDate = moment(action.payload.couponInfo.period.endDate);
        }

        /**
         *  generateLimitDay 에 따라 발급 제한 횟수 타입을 설정한다.
         */
        draft.couponCreate.couponInfo.generateLimitType = setCouponGenerateLimitType(
          action.payload.couponInfo.generateLimitDay,
        );

        /**
         *  쿠폰 유형에 따라 데이터 추가해주는게 다르다
         */
        if (action.payload.couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_PRODUCT_COUPON) {
          /**
           *  쿠폰 사용 가능한 상품 정보
           */
          draft.couponCreate.couponProductLinkInfo = {
            ...draft.couponCreate.couponProductLinkInfo,
            ...action.payload.couponProductLinkInfo,
          };

          /**
           *  업로드 아이템 중에 선택된 상품 아이템인 경우에는
           *  상품 아이템 선택 부분에서 이동을 할 수 없도록 하기
           */
          draft.couponCreate.couponProductLinkInfo.itemList.list = setUncheckableTableTransferList({
            list: draft.couponCreate.couponProductLinkInfo.itemList.list,
            selectedIdList: draft.couponCreate.couponProductLinkInfo.selectedItemList.idList,
          });

          /**
           *  상품 카테고리 SELECT LIST
           */
          draft.couponCreate.mainProductCategorySelectList = action.payload.mainProductCategorySelectList;
          draft.couponCreate.subProductCategorySelectList = action.payload.subProductCategorySelectList;
        } else if (action.payload.couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_BRAND_COUPON) {
          /**
           *  쿠폰 사용 가능한 브랜드 정보
           */
          draft.couponCreate.couponBrandLinkInfo = {
            ...draft.couponCreate.couponBrandLinkInfo,
            ...action.payload.couponBrandLinkInfo,
          };

          /**
           *  업로드 아이템 중에 선택된 브랜드 아이템인 경우에는
           *  브랜드 아이템 선택 부분에서 이동을 할 수 없도록 하기
           */
          draft.couponCreate.couponBrandLinkInfo.itemList.list = setUncheckableTableTransferList({
            list: draft.couponCreate.couponBrandLinkInfo.itemList.list,
            selectedIdList: draft.couponCreate.couponBrandLinkInfo.selectedItemList.idList,
          });
        }
      });
    },

    /**
     *  [쿠폰 생성] 쿠폰 적용 가능 상품 리스트 가져오기
     */
    [actions.GET_COUPON_APPLY_AVAILABLE_PRODUCT_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰 적용 가능 상품 리스트
         */
        draft.couponApplyAvailableProductListModal.data.listData = action.payload.listData;
        /**
         *  쿠폰적용가, 쿠폰적용 할인액 데이터 추가
         */
        draft.couponApplyAvailableProductListModal.data.listData.list = setApplyCouponDiscount(
          draft.couponApplyAvailableProductListModal.data.listData.list,
          action.payload.discountType,
          action.payload.discountAmount,
        );
      });
    },

    /**
     *  [쿠폰 상세] 쿠폰 상세 가져오기
     */
    [actions.GET_COUPON_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰 상세
         */
        draft.couponDetail.couponInfo = action.payload.couponInfo;
        /**
         *  쿠폰 기간 타입 = 시작 종료 날짜 지정 기준
         */
        if (action.payload.couponInfo.periodType === COUPON_PERIOD_TYPE.START_AND_END_DATE) {
          /**
           *  쿠폰 유효기간 설정
           */
          draft.couponDetail.couponInfo.period.startDate = moment(action.payload.couponInfo.period.startDate);
          draft.couponDetail.couponInfo.period.endDate = moment(action.payload.couponInfo.period.endDate);
        }

        /**
         *  generateLimitDay 에 따라 발급 제한 횟수 타입을 설정한다.
         */
        draft.couponDetail.couponInfo.generateLimitType = setCouponGenerateLimitType(
          action.payload.couponInfo.generateLimitDay,
        );

        /**
         *  쿠폰 통계 데이터
         */
        draft.couponDetail.couponStatistics.issued = action.payload.couponStatistics.issued;
        draft.couponDetail.couponStatistics.chartData[0].value = action.payload.couponStatistics.used;
        draft.couponDetail.couponStatistics.chartData[1].value = action.payload.couponStatistics.unUsed;

        /**
         *  쿠폰 사용내역
         */
        draft.couponDetail.couponUsageHistory = action.payload.couponUsageHistory;

        /**
         *  쿠폰 미사용내역
         */
        draft.couponDetail.couponUnUsageHistory = action.payload.couponUnUsageHistory;

        /**
         *  쿠폰 유형에 따라 데이터 추가해주는게 다르다
         */
        if (action.payload.couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_PRODUCT_COUPON) {
          /**
           *  쿠폰별 쿠폰 적용 가능 상품 정보
           */
          draft.couponDetail.couponProductLink = action.payload.couponProductLink;

          /**
           *  쿠폰적용가, 쿠폰적용 할인액 데이터 추가
           */
          draft.couponDetail.couponProductLink.list = setApplyCouponDiscount(
            draft.couponDetail.couponProductLink.list,
            action.payload.couponInfo.discountType,
            action.payload.couponInfo.discountAmount,
          );

          /**
           *  상품 1차 카테고리
           */
          draft.couponDetail.mainProductCategorySelectList = action.payload.mainProductCategorySelectList;
          /**
           *  상품 2차 카테고리
           */
          draft.couponDetail.subProductCategorySelectList = action.payload.subProductCategorySelectList;
        } else if (action.payload.couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_BRAND_COUPON) {
          /**
           *  쿠폰별 쿠폰 적용 가능 상품 정보
           */
          draft.couponDetail.couponBrandLink = action.payload.couponBrandLink;
        }
      });
    },

    /**
     *  [쿠폰 상세] 쿠폰 사용내역 리스트 요청하기
     */
    [actions.GET_COUPON_USAGE_HISTORY_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰 사용내역
         */
        draft.couponDetail.couponUsageHistory = action.payload.couponUsageHistory;
      });
    },

    /**
     *  [쿠폰 상세] 쿠폰 미사용내역 리스트 요청하기
     */
    [actions.GET_COUPON_UN_USAGE_HISTORY_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰 미사용내역
         */
        draft.couponDetail.couponUnUsageHistory = action.payload.couponUnUsageHistory;
      });
    },

    /**
     *  [쿠폰 상세] 쿠폰별 쿠폰 적용 가능 상품 정보 가져오기
     */
    [actions.GET_COUPON_PRODUCT_LINK_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰별 쿠폰 적용 가능 상품 정보
         */
        draft.couponDetail.couponProductLink = action.payload.couponProductLink;
        /**
         *  쿠폰적용가, 쿠폰적용 할인액 데이터 추가
         */
        draft.couponDetail.couponProductLink.list = setApplyCouponDiscount(
          draft.couponDetail.couponProductLink.list,
          action.payload.discountType,
          action.payload.discountAmount,
        );
      });
    },

    /**
     *  [쿠폰 상세] 쿠폰별 쿠폰 적용 가능 브랜드 정보 가져오기
     */
    [actions.GET_COUPON_BRAND_LINK_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰별 쿠폰 적용 가능 브랜드 정보
         */
        draft.couponDetail.couponBrandLink = action.payload.couponBrandLink;
      });
    },

    /**
     * 쿠폰에 연결된 상품 또는 브랜드를 리셋
     */
    [actions.RESET_COUPON_RELATED_ITEM_LIST]: (state) => {
      return produce(state, (draft) => {
        // 상품
        draft.couponCreate.couponProductLinkInfo.itemList = {
          ...draft.couponCreate.couponProductLinkInfo.itemList,
          list: [],
        };

        draft.couponCreate.couponProductLinkInfo.selectedItemList = {
          list: [],
          idList: [],
        };

        draft.couponCreate.couponProductLinkInfo.selectedItemBrandCount = 0;

        // 브랜드
        draft.couponCreate.couponBrandLinkInfo.itemList = {
          ...draft.couponCreate.couponBrandLinkInfo.itemList,
          list: [],
        };

        draft.couponCreate.couponBrandLinkInfo.selectedItemList = {
          list: [],
          idList: [],
        };
      });
    },

    /**
     *  [쿠폰 생성] 쿠폰 별 쿠폰 적용 가능 상품 아이템 리스트 요청하기
     */
    [actions.GET_COUPON_PRODUCT_ITEM_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  상품 리스트
         */
        draft.couponCreate.couponProductLinkInfo.itemList = {
          ...draft.couponCreate.couponProductLinkInfo.itemList,
          ...action.payload.couponProductLinkInfo,
        };

        /**
         *  상품 1차 카테고리
         */
        draft.couponCreate.mainProductCategorySelectList = action.payload.mainProductCategorySelectList;
        /**
         *  상품 2차 카테고리
         */
        draft.couponCreate.subProductCategorySelectList = action.payload.subProductCategorySelectList;

        /**
         *  쿠폰 유형, 혜택을 수정했을 경우에는 선택된 아이템 테이블을 초기화시켜주어야한다.
         */
        if (action.payload.isSelectedProductClear) {
          draft.couponCreate.couponProductLinkInfo.selectedItemList = {
            list: [],
            idList: [],
          };
          draft.couponCreate.couponProductLinkInfo.selectedItemBrandCount = 0;
        } else {
          /**
           *  filterOption, sortOption, pagination 을 조작한 경우
           */
          /**
           *  선택할 상품 아이템 중에 이미 설정된 상품 아이템인 경우에는
           *  상품 아이템 선택 부분에서 이동을 할 수 없도록 하기
           */
          draft.couponCreate.couponProductLinkInfo.itemList.list = setUncheckableTableTransferList({
            list: draft.couponCreate.couponProductLinkInfo.itemList.list,
            selectedIdList: draft.couponCreate.couponProductLinkInfo.selectedItemList.idList,
          });
        }
      });
    },

    /**
     *  [쿠폰 생성] 쿠폰 별 쿠폰 적용 가능 브랜드 아이템 리스트 요청하기
     */
    [actions.GET_COUPON_BRAND_ITEM_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  브랜드 리스트
         */
        draft.couponCreate.couponBrandLinkInfo.itemList = {
          ...draft.couponCreate.couponBrandLinkInfo.itemList,
          ...action.payload.couponBrandLinkInfo,
        };

        /**
         *  쿠폰 유형, 혜택을 수정했을 경우에는 선택된 아이템 테이블을 초기화시켜주어야한다.
         */
        if (action.payload.isSelectedBrandClear) {
          draft.couponCreate.couponBrandLinkInfo.selectedItemList = {
            list: [],
            idList: [],
          };
        } else {
          /**
           *  선택할 브랜드 아이템 중에 이미 설정된 브랜드 아이템인 경우에는
           *  브랜드 아이템 선택 부분에서 이동을 할 수 없도록 하기
           */
          draft.couponCreate.couponBrandLinkInfo.itemList.list = setUncheckableTableTransferList({
            list: draft.couponCreate.couponBrandLinkInfo.itemList.list,
            selectedIdList: draft.couponCreate.couponBrandLinkInfo.selectedItemList.idList,
          });
        }
      });
    },

    /**
     *  [쿠폰 발급] 발급할 쿠폰 리스트 요청하기
     */
    [actions.GET_ISSUED_COUPON_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰 전체 리스트를 넣어준다.
         */
        draft.searchCouponListModal.data.listData = action.payload.couponList;
      });
    },

    /**
     *  [쿠폰 발급] 쿠폰 발급받을 유저 리스트 요청하기
     */
    [actions.GET_GENERATE_COUPON_USER_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰 발급받을 유저 리스트
         */
        draft.couponGenerate.issuedUser.userList = action.payload.userList;
      });
    },

    /**
     *  [쿠폰 발급] 쿠폰 발급 준비 데이터 가져오기
     */
    [actions.GET_PREPARATION_GENERATE_COUPON_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  쿠폰 발급받을 유저 리스트
         */
        draft.couponGenerate.issuedUser.userList = action.payload.userList;
        /**
         *  선택된 쿠폰 발급받을 유저 리스트 초기화
         */
        draft.couponGenerate.issuedUser.selectedUserIdList = [];
        /**
         *  발급할 쿠폰 리스트
         */
        draft.couponGenerate.couponList.issuedCouponList = action.payload.issuedCouponList;
        /**
         *  선택된 발급할 쿠폰 리스트
         */
        draft.couponGenerate.couponList.selectedCouponIdList = action.payload.issuedCouponList.map((item) => item.id);
        /**
         * 선택된 쿠폰 정보 리스트 업데이트
         */
        draft.couponGenerate.couponList.selectedCouponInfoList = action.payload.issuedCouponList;

        /**
         *  기본적으로 issuedCount 를 1로 초기화한다.
         */
        draft.couponGenerate.couponList.selectedCouponInfoList =
          draft.couponGenerate.couponList.selectedCouponInfoList.map((item) => {
            return { ...item, issuedCount: 1 };
          });
      });
    },

    /**
     *  [쿠폰 발급] 검색으로 선택한 쿠폰 리스트 가져오기
     */
    [actions.GET_SEARCH_COUPON_RESULT_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  발급할 쿠폰 리스트
         */
        draft.couponGenerate.couponList.issuedCouponList = uniqBy(
          [...draft.couponGenerate.couponList.issuedCouponList, ...action.payload.issuedCouponList],
          'id',
        );

        /**
         *  선택된 발급할 쿠폰 리스트 + 모달에서 추가로 선택한 쿠폰 리스트
         */
        draft.couponGenerate.couponList.selectedCouponIdList = [
          ...draft.couponGenerate.couponList.selectedCouponIdList,
          ...action.payload.selectedCouponIdList,
        ];

        /**
         *  선택된 발급할 쿠폰 리스트 중복 제거
         */
        draft.couponGenerate.couponList.selectedCouponIdList = uniq(
          draft.couponGenerate.couponList.selectedCouponIdList,
        );

        /**
         * 선택된 쿠폰 정보 리스트 업데이트
         */
        draft.couponGenerate.couponList.selectedCouponInfoList = uniqBy(
          [...draft.couponGenerate.couponList.selectedCouponInfoList, ...action.payload.issuedCouponList],
          'id',
        );

        /**
         *  기본적으로 issuedCount 를 1로 초기화한다.
         */
        draft.couponGenerate.couponList.selectedCouponInfoList =
          draft.couponGenerate.couponList.selectedCouponInfoList.map((item) => {
            return { ...item, issuedCount: 1 };
          });
      });
    },

    /**
     *  [쿠폰 발급] 엑셀 일괄 쿠폰 발급 요청하기
     */
    [actions.GENERATE_COUPON_SUCCESS]: (state) => {
      return produce(state, (draft) => {
        /**
         *  선택된 쿠폰 발급받을 유저 id 리스트 초기화
         */
        draft.couponGenerate.issuedUser.selectedUserIdList = [];
        /**
         *  쿠폰 리스트 초기화
         */
        draft.couponGenerate.couponList.issuedCouponList = [];
        /**
         *  선택된 발급할 쿠폰 리스트 초기화
         */
        draft.couponGenerate.couponList.selectedCouponIdList = [];
        /**
         *  유저 리스트 초기화
         */
        draft.couponGenerate.issuedUser.userList = {
          ...draft.couponGenerate.issuedUser.userList,
          /**
           *  리스트
           */
          list: [],
          /**
           *  필터 옵션
           */
          filterOption: COUPON_GENERATE_USER_FILTER_OPTION_MODEL,
          /**
           *  리스트 pagination
           */
          pagination: LIST_PAGINATION_MODEL,
          /**
           *  집계 데이터
           */
          summary: LIST_SUMMARY_MODEL,
        };
        /**
         *  선택된 쿠폰 발급받을 유저 정보 리스트 초기화
         */
        draft.couponGenerate.issuedUser.selectedUserInfoList = [];
        /**
         *  선택된 발급할 쿠폰 정보 리스트(발급 수량, id) 초기화
         */
        draft.couponGenerate.couponList.selectedCouponInfoList = [];
      });
    },

    /**
     *  [쿠폰 발급 내역 리스트] 쿠폰 발급 내역 리스트 가져오기
     */
    [actions.GET_COUPON_ISSUE_HISTORY_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.couponIssueHistoryList = {
          ...draft.couponIssueHistoryList,
          ...action.payload.couponIssueHistoryList,
        };
        /**
         *  admin select 리스트
         */
        draft.adminSelectList = action.payload.adminSelectList;
      });
    },

    /**
     *  [쿠폰 발급 내역 상세] 쿠폰 발급 내역 상세 가져오기
     */
    [actions.GET_COUPON_ISSUE_HISTORY_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.couponIssueHistoryDetail = action.payload.couponIssueHistoryDetail;
      });
    },

    /**
     *  [쿠폰 발급 내역 상세] 쿠폰 발급 리스트 요청하기
     */
    [actions.GET_COUPON_ISSUE_HISTORY_COUPON_GENERATED_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.couponIssueHistoryDetail.couponIssueHistoryItemList =
          action.payload.couponIssueHistoryDetail.couponIssueHistoryItemList;
      });
    },

    /**
     *  모달 토글
     */
    [actions.TOGGLE_MODAL]: (state, action) => {
      return produce(state, (draft) => {
        action.payload.data && updateStoreWithPath([action.payload.name, 'data'], action.payload.data, draft);
        updateStoreWithPath([action.payload.name, 'isOpen'], action.payload.value, draft);
      });
    },
  },
  initialState,
);
